import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuthStore } from '@store'
import apiClient from '@services/api/client'
import PricingGrid from '@components/payment/PricingGrid'
import PaymentMethodSelector from '@components/payment/PaymentMethodSelector'
import OrderSummary from '@components/payment/OrderSummary'
import WaveQRModal from '@components/payment/WaveQRModal'

function PaymentVideoPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const { postData, postId, initialDuration } = location.state || {}

  const [selectedMethod, setSelectedMethod] = useState('')
  const [waveUrl, setWaveUrl] = useState(null)
  const [showQR, setShowQR] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [countryCode, setCountryCode] = useState('+225')
  const [isProcessing, setIsProcessing] = useState(false)
  const [duration, setDuration] = useState(initialDuration || 7)
  const [pricing, setPricing] = useState({ 1: 1500, 3: 4000, 7: 8000, 30: 25000 })
  const [isLoadingPricing, setIsLoadingPricing] = useState(true)

  const totalPrice = pricing[duration] || 8000

  useEffect(() => {
    loadPricing()
  }, [])

  const loadPricing = async () => {
    try {
      setIsLoadingPricing(true)
      const result = await apiClient.get('/tarifs')
      if (result.success && result.data) {
        const tarifs = result.data['V'] || []
        const pricingData = {}
        tarifs.forEach(t => {
          pricingData[t.nb_jours] = parseFloat(t.montant_Tarifs)
        })
        if (Object.keys(pricingData).length > 0) setPricing(pricingData)
      }
    } catch (error) {
      console.error('Erreur chargement tarifs:', error)
    } finally {
      setIsLoadingPricing(false)
    }
  }

  const handlePayment = async () => {
    if (!selectedMethod) {
      toast.error('Veuillez sélectionner un moyen de paiement')
      return
    }

    setIsProcessing(true)
    try {
      if (selectedMethod === 'wave') {
        // Wave refuse localhost → en dev, définir VITE_PUBLIC_APP_URL (domaine public HTTPS).
        const baseUrl = import.meta.env.VITE_PUBLIC_APP_URL || window.location.origin
        const successUrl = `${baseUrl}/payment-success?postId=${postId || ''}&duration=${duration}&type=video`
        const errorUrl   = `${baseUrl}/payment-error?postId=${postId || ''}&type=video`

        const result = await apiClient.post('/payment/wave', {
          amount:      totalPrice,
          success_url: successUrl,
          error_url:   errorUrl,
          post_id:     postId || null,
          duration:    duration,
        })

        if (result.success && result.wave_url) {
          if (result.dev_mode) {
            window.location.href = result.wave_url
            return
          }
          setWaveUrl(result.wave_url)
          setShowQR(true)
        } else {
          toast.error(result.message || 'Impossible de créer la session Wave.')
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors du paiement.')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fefcf8] via-blue-50 to-indigo-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-[#001F3F] mb-2">Finaliser votre vidéo</h1>
          <p className="text-gray-600 text-lg">Choisissez la durée et le mode de paiement</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <div className="lg:col-span-2 space-y-6">
            <PricingGrid
              pricing={pricing}
              selectedDuration={duration}
              onDurationChange={setDuration}
              type="video"
            />
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <PaymentMethodSelector
                selectedMethod={selectedMethod}
                onMethodChange={setSelectedMethod}
                phoneNumber={phoneNumber}
                onPhoneNumberChange={setPhoneNumber}
                countryCode={countryCode}
                onCountryCodeChange={setCountryCode}
              />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              <OrderSummary
                title={postData?.title || 'Vidéo'}
                category={postData?.category || 'Vidéo'}
                duration={duration}
                price={totalPrice}
                type="video"
              />
              <div className="space-y-3">
                <button
                  onClick={handlePayment}
                  disabled={!selectedMethod || isProcessing}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Traitement en cours...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Payer {totalPrice.toLocaleString('fr-FR')} FCFA
                    </>
                  )}
                </button>
                <button type="button" onClick={() => navigate(-1)} disabled={isProcessing}
                  className="w-full px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-all disabled:opacity-50">
                  ← Retour
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showQR && waveUrl && (
        <WaveQRModal
          waveUrl={waveUrl}
          postId={postId}
          amount={totalPrice}
          onClose={() => setShowQR(false)}
        />
      )}
    </div>
  )
}

export default PaymentVideoPage
